/**
 * Community / profile short-video capture + optional client compress.
 *
 * Strategy:
 * - Cap camera capture at 720p + ~2 Mbps (cheap; no post-process for in-app records)
 * - For library uploads: only re-encode when over maxBytes
 */

export const COMMUNITY_VIDEO_MAX_WIDTH = 1280;
export const COMMUNITY_VIDEO_MAX_HEIGHT = 720;
export const COMMUNITY_VIDEO_BITS_PER_SECOND = 2_000_000; // ~2 Mbps — fine for ≤60s shorts
export const COMMUNITY_VIDEO_AUDIO_BITS_PER_SECOND = 96_000;

/** getUserMedia constraints for in-app recording */
export const COMMUNITY_VIDEO_CAPTURE_CONSTRAINTS = {
  video: {
    facingMode: 'user',
    width: { ideal: COMMUNITY_VIDEO_MAX_WIDTH, max: COMMUNITY_VIDEO_MAX_WIDTH },
    height: { ideal: COMMUNITY_VIDEO_MAX_HEIGHT, max: COMMUNITY_VIDEO_MAX_HEIGHT },
    frameRate: { ideal: 30, max: 30 },
  },
  audio: true,
};

export function pickCommunityRecorderMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * MediaRecorder options with bitrate caps. Falls back without bitrates if unsupported.
 */
export function buildCommunityRecorderOptions(mimeType) {
  const base = mimeType ? { mimeType } : {};
  return {
    ...base,
    videoBitsPerSecond: COMMUNITY_VIDEO_BITS_PER_SECOND,
    audioBitsPerSecond: COMMUNITY_VIDEO_AUDIO_BITS_PER_SECOND,
  };
}

/**
 * Create a MediaRecorder with bitrate caps; retry without rates if the browser rejects them.
 * @param {MediaStream} stream
 * @returns {{ recorder: MediaRecorder, mimeType: string }}
 */
export function createCappedMediaRecorder(stream) {
  const mimeType = pickCommunityRecorderMimeType();
  const preferred = buildCommunityRecorderOptions(mimeType);
  try {
    return {
      recorder: new MediaRecorder(stream, preferred),
      mimeType: preferred.mimeType || mimeType || '',
    };
  } catch {
    try {
      const fallback = mimeType ? { mimeType } : undefined;
      return {
        recorder: fallback ? new MediaRecorder(stream, fallback) : new MediaRecorder(stream),
        mimeType: mimeType || '',
      };
    } catch (err) {
      const fail = new Error(err?.message || 'Could not start recording.');
      throw fail;
    }
  }
}

function attachVideoSource(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    video.onloadeddata = () => resolve({ video, url });
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that video file.'));
    };
  });
}

function scaleDimensions(width, height, maxWidth, maxHeight) {
  const w = width || maxWidth;
  const h = height || maxHeight;
  const scale = Math.min(1, maxWidth / w, maxHeight / h);
  return {
    width: Math.max(2, Math.round((w * scale) / 2) * 2),
    height: Math.max(2, Math.round((h * scale) / 2) * 2),
  };
}

/**
 * Re-encode a short clip at capped resolution + bitrate (canvas + MediaRecorder).
 * @param {File|Blob} file
 * @param {{ maxSeconds?: number, durationHint?: number|null, maxWidth?: number, maxHeight?: number, videoBitsPerSecond?: number, onProgress?: (ratio: number) => void }} [options]
 * @returns {Promise<File>}
 */
export async function reencodeCommunityVideo(
  file,
  {
    maxSeconds = 60,
    durationHint = null,
    maxWidth = COMMUNITY_VIDEO_MAX_WIDTH,
    maxHeight = COMMUNITY_VIDEO_MAX_HEIGHT,
    videoBitsPerSecond = COMMUNITY_VIDEO_BITS_PER_SECOND,
    onProgress,
  } = {}
) {
  if (!file) throw new Error('No video selected.');
  if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined') {
    throw new Error('Video compression is not supported in this browser.');
  }

  const mimeType = pickCommunityRecorderMimeType();
  if (!mimeType) {
    throw new Error('Video compression is not supported in this browser.');
  }

  let knownDuration =
    Number.isFinite(durationHint) && durationHint > 0 ? durationHint : null;
  if (!knownDuration) {
    const { readVideoDuration } = await import('./readVideoDuration');
    try {
      knownDuration = await readVideoDuration(file, {
        fallbackSeconds: maxSeconds,
        timeoutMs: 5000,
      });
    } catch {
      knownDuration = maxSeconds;
    }
  }

  const duration = Math.min(knownDuration, maxSeconds + 0.5);
  const { video, url } = await attachVideoSource(file);

  try {
    if (!video.videoWidth) {
      await new Promise((resolve) => {
        if (video.videoWidth) {
          resolve();
          return;
        }
        video.onloadeddata = () => resolve();
        video.onerror = () => resolve();
      });
    }

    const { width, height } = scaleDimensions(
      video.videoWidth,
      video.videoHeight,
      maxWidth,
      maxHeight
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare video compressor.');

    video.muted = false;
    video.currentTime = 0;
    await video.play().catch(() => {});

    const canvasStream = canvas.captureStream(30);
    try {
      if (typeof video.captureStream === 'function') {
        const mediaStream = video.captureStream();
        mediaStream.getAudioTracks().forEach((track) => canvasStream.addTrack(track));
      }
    } catch {
      /* audio optional */
    }

    let recorder;
    try {
      recorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond,
        audioBitsPerSecond: COMMUNITY_VIDEO_AUDIO_BITS_PER_SECOND,
      });
    } catch {
      recorder = new MediaRecorder(canvasStream, { mimeType });
    }

    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };

    const recorded = new Promise((resolve, reject) => {
      recorder.onerror = () => reject(new Error('Could not compress video.'));
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType });
        resolve(blob);
      };
    });

    recorder.start(200);

    const started = performance.now();
    await new Promise((resolve, reject) => {
      const draw = () => {
        try {
          ctx.drawImage(video, 0, 0, width, height);
        } catch (err) {
          reject(err);
          return;
        }
        if (onProgress && duration > 0) {
          onProgress(Math.min(1, video.currentTime / duration));
        }
        if (
          video.ended ||
          video.currentTime >= duration ||
          performance.now() - started > (maxSeconds + 3) * 1000
        ) {
          resolve();
          return;
        }
        requestAnimationFrame(draw);
      };

      video.onended = () => resolve();
      requestAnimationFrame(draw);
    });

    video.pause();
    if (recorder.state !== 'inactive') recorder.stop();
    canvasStream.getTracks().forEach((t) => t.stop());

    const blob = await recorded;
    if (!blob.size) throw new Error('Compressed video was empty. Please try again.');

    const ext = (recorder.mimeType || mimeType).includes('mp4') ? 'mp4' : 'webm';
    const type = recorder.mimeType || mimeType;
    return new File([blob], `clip-compressed-${Date.now()}.${ext}`, { type });
  } finally {
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

/**
 * Return the original file when under maxBytes; otherwise re-encode once.
 * @returns {Promise<{ file: File, compressed: boolean }>}
 */
export async function maybeCompressCommunityVideo(
  file,
  { maxBytes, maxSeconds = 60, durationHint = null, onProgress } = {}
) {
  if (!file) throw new Error('No video selected.');
  const limit = Number(maxBytes);
  if (!Number.isFinite(limit) || limit <= 0 || file.size <= limit) {
    return {
      file:
        file instanceof File
          ? file
          : new File([file], `clip-${Date.now()}.webm`, { type: file.type || 'video/webm' }),
      compressed: false,
    };
  }

  const compressed = await reencodeCommunityVideo(file, {
    maxSeconds,
    durationHint,
    onProgress,
  });

  // If still over (rare), keep the smaller of the two
  if (compressed.size < file.size) {
    return { file: compressed, compressed: true };
  }
  return {
    file:
      file instanceof File
        ? file
        : new File([file], `clip-${Date.now()}.webm`, { type: file.type || 'video/webm' }),
    compressed: false,
  };
}
